import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import CreateAccount from "./Components/CreateAccount";
import ForgotPasswordFlow from "./Components/ForgotPasswordFlow";
import ResetPassword from "./Components/ResetPassword";
import LoginAccount from "./Components/LoginAccount";
import AdminLanding from "./Components/AdminLanding";
import Apps from "./Components/Apps";
import IntelligenceHub from "./Components/IntelligenceHub";
import OperatorsMarketplace from "./Components/OperatorsMarketplace";
import AdminPanel from "./Components/AdminPanel";
import ResumeRanking from "./Components/ResumeRanking";
import CreditChecker from "./Components/CreditChecker";
import VideoConverter from "./Components/VideoConverter";
import DocsInsights from "./Components/DocsInsights";
import NewsAnalysis from "./Components/NewsAnalysis";
import MonitoringDashboard from "./Components/MonitoringDashboard";
import SurveyInsights from "./Components/SurveyInsights";
import CustomerService from "./Components/CustomerService";
import UserRolesPermissions from "./Components/AccessControl/UserRolesPermissions";
import InvitationAcceptance from "./Components/AccessControl/InvitationAcceptance";
import LoanProcessing from "./Components/LoanProcessing";
import AssetRecordsIntelligence from "./Components/AssetRecordsIntelligence";
import ProjectReports from "./Components/ProjectReports";
import CampaignInsights from "./Components/CampaignInsights";
import CaseWorkflowDashboard from "./Components/CaseWorkflow/CaseWorkflowDashboard";
import OperatorLaunch from "./Components/OperatorLaunch";
import TaskFlowEditor from "./Components/TaskFlowEditor";
import LinkedDataApps from "./Components/LinkedDataApps";
import OntologyHome from "./Components/Ontology/OntologyHome";
import OntologyPage from "./Components/Ontology/OntologyPage";
import OntologySearchPage from "./Components/Ontology/OntologySearchPage";
import OntologyDetailPage from "./Components/Ontology/OntologyDetailPage";
import OntologyAssistantLayout from "./Components/Ontology/OntologyAssistantLayout";
import BenchmarkDashboard from "./Components/Benchmark/BenchmarkDashboard";
import DefaultProjectRedirect from "./Components/DefaultProjectRedirect";

/**
 * A signed-in visitor at the root goes to their workspace; everyone else is
 * still sent to signup, which is where the unauthenticated entry point has
 * always been.
 */
const RootRedirect = () =>
    localStorage.getItem("token")
        ? <Navigate to="/workspace" replace />
        : <Navigate to="/signup" replace />;

const PageRoutes = () => (
    <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/signup" element={<CreateAccount />} />
        <Route path="/forgotpassword" element={<ForgotPasswordFlow />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/login" element={<LoginAccount />} />
        <Route path="/AIStudio" element={<AdminLanding />} />
        <Route path="/AIStudio/customize" element={<AdminLanding isEditor={true} />} />
        <Route path="/AIStudio/task-flow-editor" element={<TaskFlowEditor />} />
        <Route path="/benchmark-dashboard" element={<BenchmarkDashboard />} />
        <Route path="/benchmark-dashboard/:runKey" element={<BenchmarkDashboard />} />
        <Route path="/Apps" element={<Apps />} />
        {/* Resolves the user's default project and forwards into it. */}
        <Route path="/workspace" element={<DefaultProjectRedirect />} />
        <Route path="/intelligence-hub" element={<IntelligenceHub />} />
        <Route path="/operators-marketplace" element={<OperatorsMarketplace />} />
        <Route path="/linked-data-apps" element={<LinkedDataApps />} />
        {/*
          Ontology. Codes are addressable so the agents drawer can deep-link to a
          concept, policy or parameter; :kind is one of concepts|policies|parameters.
          The detail route must be declared after the browse routes so 'concepts'
          alone is not read as a code.

          /ontology is a landing page rather than a redirect to the first tab: the tabs
          are named after the data model, and this is where the user's QUESTION gets
          asked instead. /ontology/search is static, so React Router ranks it above
          /ontology/:kind and 'search' is never taken for a kind.

          Concept CATEGORIES stay in the query string (?concept_type=risk_signal) and
          are deliberately not a path segment: /ontology/concepts/red-flags would match
          /ontology/:kind/:code and be read as a concept whose code is 'red-flags'.

          The layout route holds the Knowledge Assistant, so one chat session spans
          every page under /ontology.
        */}
        <Route path="/ontology" element={<OntologyAssistantLayout />}>
            <Route index element={<OntologyHome />} />
            <Route path="search" element={<OntologySearchPage />} />
            <Route path=":kind" element={<OntologyPage />} />
            <Route path=":kind/:code" element={<OntologyDetailPage />} />
        </Route>
        <Route path="/AIStudioSetup" element={<AdminPanel />} />
        <Route path="/analytics/projects/:projectId" element={<ProjectReports />} />
        <Route path="/analytics/projects/:projectId/campaign/:campaignId" element={<CampaignInsights />} />
        <Route path="/analytics/projects/:projectId/case-workflow" element={<CaseWorkflowDashboard />} />
        <Route path="/operator-launch/:operatorId" element={<OperatorLaunch />} />

        {/* AI Apps Routes */}
        <Route path="/resume-ranking" element={<ResumeRanking />} />
        <Route path="/news-analysis" element={<NewsAnalysis />} />
        <Route path="/credit-checker" element={<CreditChecker />} />
        <Route path="/video-converter" element={<VideoConverter />} />
        <Route path="/docs-insights" element={<DocsInsights />} />

        <Route path="/survey-insights" element={<SurveyInsights />} />
        <Route path="/data-analytics" element={<SurveyInsights projectType="DATA_ANAL" />} />
        <Route path="/monitoringDashboard" element={<MonitoringDashboard />} />
        <Route path="/customer-service" element={<CustomerService />} />
        <Route path="/access-control" element={<UserRolesPermissions />} />
        <Route path="/invitation/accept" element={<InvitationAcceptance />} />
        <Route path="/loan-processing" element={<LoanProcessing />} />
        <Route path="/asset-records-intelligence" element={<AssetRecordsIntelligence />} />
    </Routes>
);

export default PageRoutes;
